import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { rolloverAPI } from '../../api/endpoints';
import { formatTimeAgo, formatXPUNT } from '../../utils/helpers';
import { FaPlus, FaChartLine, FaUsers, FaClock, FaCheck, FaArrowRight } from 'react-icons/fa';
import LoadingSpinner from '../common/LoadingSpinner';
import './RolloverList.css';

const RolloverList = () => {
  const { isAuthenticated, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [games, setGames] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, pages: 0, current: 1 });

  const fetchGames = async () => {
    setLoading(true);
    try {
      const params = {
        per_page: 20,
        page: pagination.current,
      };
      const response = await rolloverAPI.getList(params);
      setGames(response.data.data || []);
      setPagination({
        total: response.data.total || 0,
        pages: response.data.pages || 0,
        current: pagination.current,
      });
    } catch (error) {
      console.error('Failed to fetch rollover games:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchGames();
  }, [pagination.current]);

  const handlePageChange = (page) => {
    setPagination(prev => ({ ...prev, current: page }));
  };

  if (loading && pagination.current === 1) {
    return (
      <div className="rollover-list-loading">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  return (
    <div className="rollover-list-container">
      <div className="rollover-list-header">
        <div>
          <h2><FaChartLine className="header-icon" /> Rollover Games</h2>
          <p className="subtitle">Follow sequential bet code challenges from creators</p>
        </div>
        {isAuthenticated && user?.punter_plus && (
          <Link to="/rollover/create" className="btn btn-primary">
            <FaPlus /> Create Rollover
          </Link>
        )}
      </div>

      {/* Results */}
      <div className="results-info">
        <span>{pagination.total} rollover games found</span>
      </div>

      {/* Games Grid */}
      {games.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">🔄</div>
          <h3>No rollover games found</h3>
          <p>Be the first to create a rollover challenge</p>
        </div>
      ) : (
        <div className="rollover-grid">
          {games.map((game) => (
            <Link to={`/rollover/${game.id}`} key={game.id} className="rollover-card">
              <div className="rollover-card-header">
                <span className={`rollover-status ${game.status}`}>
                  {game.status === 'active' ? '🟢 Active' : 
                   game.status === 'completed' ? '✅ Completed' : 
                   '❌ Failed'}
                </span>
                {game.is_private && (
                  <span className="rollover-private">🔒 Private</span>
                )}
              </div>
              <h3 className="rollover-title">{game.title}</h3>
              <p className="rollover-description">
                {game.description?.length > 100 
                  ? `${game.description.substring(0, 100)}...` 
                  : game.description}
              </p>
              <div className="rollover-stats">
                <div className="stat">
                  <FaUsers className="stat-icon" />
                  <span>{game.subscribers_count || 0} followers</span>
                </div>
                <div className="stat">
                  <FaClock className="stat-icon" />
                  <span>{game.total_bets || 0} bets</span>
                </div>
                <div className="stat">
                  <FaCheck className="stat-icon" />
                  <span>{game.current_streak || 0} streak</span>
                </div>
              </div>
              <div className="rollover-footer">
                <span className="rollover-creator">
                  By {game.creator_name || 'Unknown'}
                </span>
                <span className="rollover-time">
                  {formatTimeAgo(game.created_at)}
                </span>
              </div>
            </Link>
          ))}
        </div>
      )}

      {/* Pagination */}
      {pagination.pages > 1 && (
        <div className="pagination">
          <button 
            onClick={() => handlePageChange(pagination.current - 1)}
            disabled={pagination.current === 1}
            className="pagination-btn"
          >
            Previous
          </button>
          <div className="pagination-pages">
            {Array.from({ length: Math.min(pagination.pages, 5) }, (_, i) => {
              let pageNum;
              if (pagination.pages <= 5) {
                pageNum = i + 1;
              } else if (pagination.current <= 3) {
                pageNum = i + 1;
              } else if (pagination.current >= pagination.pages - 2) {
                pageNum = pagination.pages - 4 + i;
              } else {
                pageNum = pagination.current - 2 + i;
              }
              return (
                <button
                  key={pageNum}
                  onClick={() => handlePageChange(pageNum)}
                  className={`pagination-page ${pagination.current === pageNum ? 'active' : ''}`}
                >
                  {pageNum}
                </button>
              );
            })}
          </div>
          <button 
            onClick={() => handlePageChange(pagination.current + 1)}
            disabled={pagination.current === pagination.pages}
            className="pagination-btn"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default RolloverList;